/**
 * Predicate kind metadata shared between client authoring UI and migration helpers.
 */

import type { Predicate } from "@/shared/eval-matching";
import {
  isTurnScopablePredicateKind,
  TURN_SCOPABLE_PREDICATE_KINDS,
} from "@sphinx/sdk/predicates";

export type PredicateKind = Predicate["type"];

export const PREDICATE_KIND_LABELS: Record<PredicateKind, string> = {
  toolCalledWith: "Tool was called with…",
  toolCalledAtLeastOnce: "Tool was called at least once",
  toolNeverCalled: "Tool was never called",
  firstToolWas: "First tool called was…",
  responseContains: "Response contains…",
  responseMatches: "Response matches regex…",
  noToolErrors: "No tool errors",
  finalAssistantMessageNonEmpty: "Final message non-empty",
  tokenBudgetUnder: "Token budget under N",
  widgetRendered: "View rendered",
  widgetRenderLatencyUnder: "View rendered under N ms",
  widgetNoConsoleErrors: "No view console errors",
  // "Fewer than", not "maximum": the comparator is a strict `<`, so
  // `turnCountUnder: 3` passes at 2 turns and fails at 3. "Maximum 3" would
  // read as inclusive and mislead every author who set it.
  turnCountUnder: "Fewer than N user turns",
};

/**
 * The TURN-SCOPED variant of {@link PREDICATE_KIND_LABELS} — not a fork of it.
 *
 * Only the kinds whose meaning actually narrows when they are evaluated at a
 * point in the flow rather than over the finished transcript appear here: "no
 * tool errors" over the whole run and "no tool errors *so far*" are different
 * claims, and a step-scoped row that borrowed the whole-run wording would
 * overstate what it checked. Every other kind falls through to the canonical
 * label via {@link labelForInlineAssert}, so adding a predicate kind needs no
 * entry here unless it has that same scope-sensitivity.
 */
export const INLINE_ASSERT_LABELS: Partial<Record<PredicateKind, string>> = {
  noToolErrors: "No tool errors so far",
  widgetNoConsoleErrors: "No view console errors so far",
};

export const STEP_ASSERT_PREDICATE_KINDS: readonly PredicateKind[] = [
  ...TURN_SCOPABLE_PREDICATE_KINDS,
] as PredicateKind[];

export function isScenarioPredicateKind(kind: PredicateKind): boolean {
  return isTurnScopablePredicateKind(kind) && kind !== "tokenBudgetUnder";
}

export const PREDICATE_KIND_ORDER: PredicateKind[] = [
  "toolCalledWith",
  "toolCalledAtLeastOnce",
  "toolNeverCalled",
  "firstToolWas",
  "responseContains",
  "responseMatches",
  "noToolErrors",
  "finalAssistantMessageNonEmpty",
  "tokenBudgetUnder",
  "turnCountUnder",
  "widgetRendered",
  "widgetRenderLatencyUnder",
  "widgetNoConsoleErrors",
];

export const SYNTHETIC_MONITOR_KINDS: ReadonlySet<PredicateKind> = new Set([
  "widgetRendered",
  "widgetRenderLatencyUnder",
  "widgetNoConsoleErrors",
]);

/**
 * The swarm-level ⟷ goal-level split for swarm rubric authoring.
 *
 * A swarm-level check must hold for EVERY journey regardless of what its goal
 * is — instruments about the session itself (errors, budgets, views). A check
 * that names a tool, a phrase, or a call order is a claim about one specific
 * task: authored beside that goal, it measures; stamped swarm-wide, it drags
 * down pass rates on journeys that could never satisfy it.
 */
export const SWARM_LEVEL_PREDICATE_KINDS: readonly PredicateKind[] = [
  "noToolErrors",
  "finalAssistantMessageNonEmpty",
  "tokenBudgetUnder",
  "turnCountUnder",
  "widgetRendered",
  "widgetRenderLatencyUnder",
  "widgetNoConsoleErrors",
];

export const GOAL_LEVEL_PREDICATE_KINDS: readonly PredicateKind[] = [
  "toolCalledWith",
  "toolCalledAtLeastOnce",
  "toolNeverCalled",
  "firstToolWas",
  "responseContains",
  "responseMatches",
];

export const GLOBAL_POLICY_MENU_KINDS: readonly PredicateKind[] = [
  "tokenBudgetUnder",
  "noToolErrors",
  "widgetNoConsoleErrors",
];

/** Authoring catalog for whole-run global gates — short labels + tooltip copy. */
export type GlobalGateCatalogEntry = {
  kind: PredicateKind;
  label: string;
  /** One-line summary (tooltip title line). */
  description: string;
  /** Longer tooltip body for progressive disclosure. */
  detail: string;
};

export const GLOBAL_GATES_SECTION_HELP = {
  title: "Whole-run checks",
  paragraphs: [
    "Whole-run rules evaluated after the scenario finishes, using the full transcript.",
    "Step checks run inline at a specific point in the flow — use those for conversation and view assertions.",
    "Case checks extend suite defaults. Add here only for policies that must hold across the entire run.",
  ],
} as const;

export const GLOBAL_GATE_CATALOG: GlobalGateCatalogEntry[] = [
  {
    kind: "tokenBudgetUnder",
    label: "Token budget",
    description: "Keep total tokens under a limit",
    detail:
      "Counts input and output tokens across the whole iteration. Fails when the total is greater than or equal to your limit.",
  },
  {
    kind: "noToolErrors",
    label: "No tool errors",
    description: "No MCP or transport tool failures",
    detail:
      "Passes when no tool reported an error anywhere in the run — neither MCP isError nor a transport failure.",
  },
  {
    kind: "widgetNoConsoleErrors",
    label: "No view console errors",
    description: "Rendered views must not log console errors",
    detail:
      "Passes when no rendered view logged console errors. Fails when the run recorded no view renders. Optionally limit to one view tool.",
  },
];

/**
 * True iff `kind` is a predicate kind THIS BUILD knows, checked as an own
 * property rather than with `in` or a truthiness test on the lookup.
 *
 * Both of the lazier forms walk the prototype chain, so a persisted
 * discriminator of `"__proto__"` or `"constructor"` — reachable, because
 * `parseIterationPredicates` validates only that `type` is a string — resolves
 * to an inherited object. That object is truthy, survives `??`, and then
 * throws when React tries to render it as a text child. An unknown kind must
 * degrade to its raw string, never to whatever `Object.prototype` has.
 */
export function isKnownPredicateKind(kind: string): kind is PredicateKind {
  return Object.prototype.hasOwnProperty.call(PREDICATE_KIND_LABELS, kind);
}

export function isGlobalPolicyKind(kind: PredicateKind): boolean {
  return (GLOBAL_POLICY_MENU_KINDS as readonly string[]).includes(kind);
}

export function globalGateLabel(kind: PredicateKind): string {
  const entry = GLOBAL_GATE_CATALOG.find((g) => g.kind === kind);
  return entry?.label ?? PREDICATE_KIND_LABELS[kind];
}

export function globalGateDescription(kind: PredicateKind): string | undefined {
  return GLOBAL_GATE_CATALOG.find((g) => g.kind === kind)?.description;
}

export function globalGateDetail(kind: PredicateKind): string | undefined {
  return GLOBAL_GATE_CATALOG.find((g) => g.kind === kind)?.detail;
}

export function labelForGlobalGate(kind: PredicateKind): string {
  return globalGateLabel(kind);
}

export function labelForInlineAssert(kind: PredicateKind): string {
  return INLINE_ASSERT_LABELS[kind] ?? PREDICATE_KIND_LABELS[kind];
}

export function blankPredicate(kind: PredicateKind): Predicate {
  switch (kind) {
    case "toolCalledWith":
      return { type: "toolCalledWith", toolName: "", args: { args: {} } };
    case "toolCalledAtLeastOnce":
      return { type: "toolCalledAtLeastOnce", toolName: "" };
    case "toolNeverCalled":
      return { type: "toolNeverCalled", toolName: "" };
    case "firstToolWas":
      return { type: "firstToolWas", toolName: "" };
    case "responseContains":
      return { type: "responseContains", needle: "" };
    case "responseMatches":
      return { type: "responseMatches", pattern: "" };
    case "noToolErrors":
      return { type: "noToolErrors" };
    case "finalAssistantMessageNonEmpty":
      return { type: "finalAssistantMessageNonEmpty" };
    case "tokenBudgetUnder":
      return { type: "tokenBudgetUnder", tokens: 1000 };
    case "turnCountUnder":
      return { type: "turnCountUnder", turns: 10 };
    case "widgetRendered":
      return { type: "widgetRendered" };
    case "widgetRenderLatencyUnder":
      return { type: "widgetRenderLatencyUnder", ms: 3000 };
    case "widgetNoConsoleErrors":
      return { type: "widgetNoConsoleErrors" };
  }
}

/**
 * Human label for one rubric criterion.
 *
 * `label` is the author's own words and always wins. When absent, the
 * predicate itself is formatted with its distinguishing argument inlined —
 * "Tool was called at least once" alone is useless on a scorecard with three
 * such rows, so the tool name goes in the label.
 *
 * Accepts either a rubric entry or a bare predicate so the run scorecard
 * (which receives `{label?, kind}` without the predicate) and the authoring
 * form (which has the whole entry) can share one function.
 */
export function formatCriterion(
  entry:
    | { label?: string; predicate: Predicate }
    | { label?: string; kind: PredicateKind },
): string {
  if (entry.label !== undefined && entry.label.trim().length > 0) {
    return entry.label.trim();
  }
  if (!("predicate" in entry)) return PREDICATE_KIND_LABELS[entry.kind];

  const predicate = entry.predicate;
  const base = PREDICATE_KIND_LABELS[predicate.type];
  switch (predicate.type) {
    case "toolCalledWith":
    case "toolCalledAtLeastOnce":
    case "toolNeverCalled":
    case "firstToolWas":
      return predicate.toolName ? `${base} ${predicate.toolName}` : base;
    case "responseContains":
      return `Response contains "${predicate.needle}"`;
    case "responseMatches":
      return `Response matches /${predicate.pattern}/`;
    case "tokenBudgetUnder":
      return `Token budget under ${predicate.tokens}`;
    case "turnCountUnder":
      return `Fewer than ${predicate.turns} user turns`;
    case "widgetRenderLatencyUnder":
      return predicate.toolName
        ? `${predicate.toolName} rendered under ${predicate.ms} ms`
        : `View rendered under ${predicate.ms} ms`;
    case "widgetRendered":
    case "widgetNoConsoleErrors":
      return predicate.toolName ? `${base} (${predicate.toolName})` : base;
    default:
      return base;
  }
}

export function filterKindsForMenu(
  kinds: readonly PredicateKind[],
  syntheticMonitorsEnabled: boolean,
  allowed?: readonly PredicateKind[] | null,
): PredicateKind[] {
  const allowedSet = allowed ? new Set(allowed) : null;
  return kinds.filter(
    (kind) =>
      (syntheticMonitorsEnabled || !SYNTHETIC_MONITOR_KINDS.has(kind)) &&
      (!allowedSet || allowedSet.has(kind)),
  );
}
